import type { ComponentType, ReactNode } from 'react'
import { useState } from 'react'
import { PageLayoutMode, useLayout } from '@/contexts/LayoutContext'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/lib/utils'
import { useLocation } from 'react-router-dom'

import { ModuleTypeEnum } from '@scrambl/shared'

import type { DetailHeaderSlots } from './DetailHeader'
import { DetailHeaderActions } from './DetailHeader'
import { TabItem, useModuleNavigation } from './ModuleNavigation'
import { NavPinContext, NavPinMenu } from './NavPinMenu'
import { PageContainer } from './PageContainer'

export interface ModuleLayoutProps {
  moduleSlug: string
  /** Skip the auto "..." pin menu, e.g. on a page the viewer cannot read. */
  hidePinMenu?: boolean
  title: string | ReactNode
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  tabs?: TabItem[]
  headerChildren?: ReactNode
  isDetailView?: boolean
  isLoading?: boolean
  entityId?: string
  entityParamName?: string
  fullWidth?: boolean
  fullHeight?: boolean
  noHeaderSpacing?: boolean
  noPadding?: boolean
  noBottomPadding?: boolean
  /** Let a module's results fill the content area, including split-view lists. */
  edgeToEdge?: boolean
  titleSuffix?: string // Add optional suffix for entity name or additional context
  /** Browser-tab title override for pages that suppress the visual header title (e.g. pass title="" and set this) */
  documentTitle?: string
  condensed?: boolean
  fixed?: boolean
  enableScroll?: boolean
  /**
   * 'fill' (default) keeps the pinned-header, nested-scroller layout. 'flow'
   * makes the whole page column one scrolling document. Tabs can opt in
   * per-tab with `setLayoutOptions({ layout: 'flow' })` instead.
   */
  layout?: PageLayoutMode
  /** Icon component to display before the title (set to null to suppress auto icon resolution) */
  icon?: ComponentType<{ className?: string; weight?: string }> | null
  // Props for ModuleAvatar integration
  moduleType?: ModuleTypeEnum
  entityData?: {
    id?: string
    name?: string
    logoUrl?: string
  }
  onEntityClick?: () => void
  /** Callback for logo upload - when provided, avatar becomes editable for users with MANAGE permission */
  onLogoChange?: (file: File | null) => Promise<void>
  /** Back link configuration for detail views */
  backLink?: {
    label: string
    href: string
  }
  /** 20 px title (default) or the 32 px display title for hub landing pages (design audit S6). */
  titleSize?: 'default' | 'display'
  /** Record page header slots; the actions side renders from these instead of `actions` (design audit S7). */
  detail?: DetailHeaderSlots
  /** Render layout-context actions before the module's primary actions */
  headerActionsFirst?: boolean
  /** Decorative layer rendered absolutely behind the header block (title row + hero + tabs) */
  headerBackground?: ReactNode
  /** Full-width hero content rendered inside the header block, between the title row and the tabs — sits on `headerBackground` */
  headerHero?: ReactNode
  /** A row of its own between the title row and the tabs (e.g. a stage gauge). */
  headerStrip?: ReactNode
}

export function ModuleLayout({
  moduleSlug,
  hidePinMenu = false,
  title,
  description,
  actions,
  children,
  className,
  tabs = [],
  headerChildren,
  isDetailView = false,
  isLoading = false,
  entityId,
  entityParamName,
  fullWidth: propFullWidth,
  fullHeight: propFullHeight,
  noHeaderSpacing: propNoHeaderSpacing,
  noPadding: propNoPadding,
  noBottomPadding = false,
  edgeToEdge = false,
  titleSuffix,
  documentTitle,
  condensed = false,
  fixed = false,
  enableScroll = true,
  layout: propLayout,
  icon,
  moduleType,
  entityData,
  onEntityClick,
  onLogoChange,
  backLink,
  titleSize,
  detail,
  headerActionsFirst = false,
  headerBackground,
  headerHero,
  headerStrip,
}: ModuleLayoutProps) {
  // Update document title when component mounts or title changes
  const titleString = typeof title === 'string' ? title : 'Page'
  useDocumentTitle(documentTitle ?? titleString, titleSuffix)

  const { moduleTabs } = useModuleNavigation({
    moduleSlug,
    tabs,
    isDetailView,
    entityId,
    entityParamName,
  })

  // Get the current location to check if we're on a settings tab or discussions tab
  const location = useLocation()
  const isSettingsTab = location.pathname.includes('/settings')
  // Improved discussion tab detection that works with both standalone discussions, nested tabs, and threads
  const isDiscussionsTab = location.pathname.includes('/discussions')
  const isThreadsPage = location.pathname.includes('/threads')
  const activeTab = location.pathname.split('/').pop()
  const isNestedDiscussionsTab = activeTab === 'discussions'
  const isAnyDiscussionsTab =
    isDiscussionsTab || isNestedDiscussionsTab || isThreadsPage

  // Get layout context to check for overrides
  const { layoutOptions } = useLayout()

  // Use layout context first, then prop value, then auto-detect
  const fullWidth =
    propFullWidth ??
    layoutOptions.fullWidth ??
    (isSettingsTab || isAnyDiscussionsTab)

  // Use layout context first, then prop value, then auto-detect
  const fullHeight =
    propFullHeight ?? layoutOptions.fullHeight ?? isAnyDiscussionsTab

  // Use layout context first, then prop value, then auto-detect
  const noHeaderSpacing =
    propNoHeaderSpacing ?? layoutOptions.noHeaderSpacing ?? isAnyDiscussionsTab

  // Use layout context first, then prop value, then auto-detect
  const noPadding =
    propNoPadding ?? layoutOptions.noPadding ?? isAnyDiscussionsTab

  // Prop first, then the per-tab context opt-in (matches every other option
  // resolved above — despite the historical "context takes precedence" comment
  // in PageContainer, the prop always wins).
  const layoutMode = propLayout ?? layoutOptions.layout

  const headerActions = layoutOptions.headerActions

  // Pages that render their own overflow ("...") menu absorb "Pin to
  // sidebar" as a menu item and suppress the auto-injected menu via
  // <SuppressAutoNavPinMenu /> — headers never show two "..." triggers.
  const [autoPinMenuSuppressed, setAutoPinMenuSuppressed] = useState(false)

  // Every module page gets the overflow ("...") menu with "Pin to sidebar";
  // skipped when the title isn't a plain string (no usable pin label) or the
  // page brings its own menu.
  const pinMenu =
    !autoPinMenuSuppressed &&
    !hidePinMenu &&
    typeof title === 'string' &&
    title.trim() !== '' ? (
      <NavPinMenu label={title} />
    ) : undefined

  // Wrap actions to ensure they're responsive. The pin menu goes to the
  // header's own menu slot so phones keep it top right beside the title.
  const responsiveActions =
    actions || detail || headerActions ? (
      <NavPinContext.Provider
        value={{
          // Only a real string title makes a usable pin label — pages with JSX
          // titles must pass an explicit label to PinToSidebarMenuItem (an
          // empty context label disables the item rather than pinning "Page").
          label: typeof title === 'string' ? title : '',
          setAutoMenuSuppressed: setAutoPinMenuSuppressed,
        }}
      >
        {/* min-w-0 keeps the shrink chain intact so nested action clusters can
          wrap instead of overflowing the header on narrow windows. Always a
          wrapping ROW (never flex-col): pages pass loose sibling buttons here,
          and a phone-width column turned each one into its own full row. */}
        <div className="flex min-w-0 flex-row flex-wrap items-center gap-2">
          {headerActionsFirst && headerActions}
          {detail ? <DetailHeaderActions slots={detail} /> : actions}
          {!headerActionsFirst && headerActions}
        </div>
      </NavPinContext.Provider>
    ) : undefined

  return (
    <PageContainer
      header={{
        title,
        titleSuffix,
        description,
        tabs: moduleTabs,
        actions: responsiveActions,
        menu: pinMenu,
        children: headerChildren,
        condensed,
        fixed,
        icon,
        moduleType,
        entityData,
        onEntityClick,
        onLogoChange,
        backLink,
        titleSize,
        detail,
        background: headerBackground,
        hero: headerHero,
        strip: headerStrip,
      }}
      className={cn(
        // Only add spacing if noPadding is false and no custom className provided
        !noPadding && !edgeToEdge && !className
          ? 'space-y-4 md:space-y-6'
          : className,
      )}
      isLoading={isLoading}
      fullWidth={fullWidth}
      fullHeight={fullHeight}
      noHeaderSpacing={noHeaderSpacing}
      noPadding={noPadding}
      noBottomPadding={noBottomPadding}
      edgeToEdge={edgeToEdge}
      enableScroll={enableScroll}
      layout={layoutMode}
    >
      {children}
    </PageContainer>
  )
}
